import type { ClauseMiningDryRun, ClauseMiningDryRunFilter } from '@clm/shared-types';

import { ApiError } from '../../api/errors';
import { classifyApiError, type ApiProblemKind } from '../../api/problem';

/**
 * Shared bits for the Pipeline page's **Clause mining** section — CLM-103
 * (first slice).
 *
 * Role lists are *display hints* (`mayAttempt`, `permissions.ts`): the API's
 * action check is the gate, and an identity that names no role sees every
 * control enabled and the API's 403 (D-82-1's pattern).
 */

/** The roles the gateway grants `clause_mining:request` to. */
export const REQUEST_ROLES: readonly string[] = ['contributor', 'admin'];
/** The roles the gateway grants `clause_mining:approve` to. */
export const APPROVE_ROLES: readonly string[] = ['admin'];

/** The active card treats a heartbeat older than this as stale. */
export const STALE_HEARTBEAT_MS = 2 * 60 * 1000;
/** The active card's poll interval. */
export const ACTIVE_POLL_MS = 5_000;

export interface DryRunProblem {
  readonly kind: ApiProblemKind;
  readonly field_errors: readonly { readonly path: string; readonly message: string }[];
  readonly correlation_id: string | null;
}

export function toDryRunProblem(error: unknown): DryRunProblem {
  const body = error instanceof ApiError ? (error.body as { field_errors?: unknown } | null) : null;
  const fieldErrors = Array.isArray(body?.field_errors)
    ? (body.field_errors as { path?: unknown; message?: unknown }[])
        .filter((entry) => typeof entry.path === 'string' && typeof entry.message === 'string')
        .map((entry) => ({ path: entry.path as string, message: entry.message as string }))
    : [];
  return {
    kind: classifyApiError(error),
    field_errors: fieldErrors,
    correlation_id: error instanceof ApiError ? error.correlation_id : null,
  };
}

/** The headline for a failed control action; the 409 on request / run-now is the one-slot rule. */
export function dryRunProblemHead(problem: DryRunProblem, forbiddenCopy: string): string {
  switch (problem.kind) {
    case 'conflict':
      return problem.field_errors.some((entry) => entry.message.startsWith('dry_run_already_pending'))
        ? 'A dry run is already pending'
        : 'The API refused this — the run is no longer in that state';
    case 'not_found':
      return 'This run is no longer visible — reload';
    case 'forbidden':
      return forbiddenCopy;
    default:
      return 'The request failed';
  }
}

/** One line describing what a run covers, for the pending list and history. */
export function summariseFilter(filter: ClauseMiningDryRunFilter): string {
  switch (filter.stage) {
    case 'segment':
      return `segment · limit ${filter.limit}${filter.contract_key === undefined ? '' : ` · ${filter.contract_key}`}`;
    case 'cluster':
      return `cluster · paragraphs ${filter.paragraph_limit === undefined ? 'the whole corpus' : filter.paragraph_limit}`;
    case 'describe':
      return `describe · top ${filter.top}`;
    case 'match':
      return `match · limit ${filter.limit ?? 'default'}${filter.contract_key === undefined ? '' : ` · ${filter.contract_key}`}${
        filter.rematch_unreviewed === true ? ' · rematch unreviewed' : ''
      }`;
    default:
      return filter satisfies never;
  }
}

export function isTerminal(run: Pick<ClauseMiningDryRun, 'status'>): boolean {
  return run.status === 'completed' || run.status === 'failed' || run.status === 'cancelled';
}

/** Cut a long error summary for a table cell; the full text rides in `title`. */
export function truncate(text: string, max = 80): string {
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}

/** A one-line read of the result, per stage — what the history table and the active card show. */
export function summariseResult(run: ClauseMiningDryRun): string {
  const result = run.result;
  if (result === null) return '—';
  switch (result.stage) {
    case 'segment':
      return `${result.contracts_seen} contracts · ${result.quality_split.structural} structural / ${result.quality_split.weak} weak`;
    case 'cluster':
      return `${result.clusters_formed} clusters formed`;
    case 'describe':
      return `${result.describe_candidates.length} candidates ready to describe`;
    case 'match':
      return `${result.contracts_seen} contracts · ${result.contracts_matched} matched / ${result.contracts_skipped} skipped / ${result.contracts_failed} failed`;
    default:
      return '—';
  }
}
